import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TemplateComposer, { type TemplateComposerHandle } from 'components/templates/TemplateComposer';
import useCurrentUser from 'hooks/useCurrentUser';
import { type FC, useCallback, useRef, useState } from 'react';
import { usePostTemplateMessageMutation } from 'services/api/messages';
import { usePostScheduledTemplateMessageMutation } from 'services/api/scheduledMessages.api';
import type { Client } from 'services/types/client';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, SHADOW } from 'styles/tokens';
import capitalize from 'utils/capitalize';
import canChangeAutomaticResponses from 'utils/validations/canChangeAutomaticResponses';

import CreateTemplateForm from './CreateTemplateForm';

interface Props {
  client: Client;
  businessUnitId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  refetch: () => void;
  allowTemplateCreation: boolean;
  initialTemplateName?: string | null;
  /** Whether sending pauses the AI (agent takeover). False for the dashboard resend, which leaves AI untouched. */
  pausesAiOnSend?: boolean;
}

const TemplateFormModal: FC<Props> = ({
  client,
  businessUnitId,
  open,
  setOpen,
  refetch,
  allowTemplateCreation,
  initialTemplateName,
  pausesAiOnSend = true,
}) => {
  const composerRef = useRef<TemplateComposerHandle>(null);
  const [creating, setCreating] = useState(false);
  const currentUser = useCurrentUser();
  const [postTemplate] = usePostTemplateMessageMutation();
  const [postScheduleTemplateMessage] = usePostScheduledTemplateMessageMutation();

  // Sending a template from the conversation means the agent is taking over, so default to pausing
  // the AI (it can be re-enabled via the checkbox before sending). Resends that don't take over
  // (dashboard) pass pausesAiOnSend=false and leave the AI status untouched.
  const canChangeAiStatus = pausesAiOnSend && canChangeAutomaticResponses(client.lastLeadStatus);
  const initialAiStatus: 'enabled' | 'disabled' = 'disabled';

  const prefill = useCallback(
    (paramName: string): string => {
      if (['user', 'usuario', 'ejecutivo', 'vendedor', 'nombre_vendedor'].includes(paramName)) {
        return capitalize(currentUser?.name) || '';
      }
      if (['client', 'cliente', 'nombre_cliente', 'nombre', 'name'].includes(paramName)) {
        return capitalize(client.firstName) || '';
      }
      if (['brand', 'marca'].includes(paramName)) return capitalize(client.brandOfInterest) || '';
      if (['model', 'modelo', 'auto', 'car'].includes(paramName)) return capitalize(client.modelsOfInterest?.[0]) || '';
      if (['branch_office', 'sucursal'].includes(paramName)) return capitalize(client.branchOffice) || '';
      return '';
    },
    [client.firstName, client.brandOfInterest, client.modelsOfInterest, client.branchOffice, currentUser?.name]
  );

  const handleSubmit = () => {
    const data = composerRef.current?.getSubmitData();
    if (!data) return;
    if (data.postAt) {
      postScheduleTemplateMessage({
        clientId: client.id,
        templateName: data.templateName,
        components: data.components,
        postAt: data.postAt,
      })
        .then(() => refetch())
        .catch((err) => console.error(err));
    } else {
      postTemplate({
        clientId: client.id,
        templateName: data.templateName,
        components: data.components,
        aiStatus: data.aiStatus,
      })
        .then(() => refetch())
        .catch((err) => console.error(err));
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Modal open onClose={() => setOpen(false)}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: '70%' },
          height: '90vh',
          maxHeight: '90vh',
          overflow: 'hidden',
          backgroundColor: GLASS_BACKGROUND,
          backdropFilter: GLASS_BLUR,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          border: `1px solid ${alpha(NAVY, 0.06)}`,
          p: { xs: 2.5, md: 4 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY, fontSize: '1.15rem', mb: 2 }}>
          {creating ? 'Crear plantilla' : 'Enviar plantilla'}
        </Typography>
        <TemplateComposer
          ref={composerRef}
          businessUnitId={businessUnitId}
          prefill={prefill}
          allowTemplateCreation={allowTemplateCreation}
          createTemplateSlot={<CreateTemplateForm businessUnitId={businessUnitId} currentClient={client} />}
          showDelete={allowTemplateCreation}
          autoSelectFirst
          initialTemplateName={initialTemplateName}
          showSchedule
          ai={canChangeAiStatus ? { initial: initialAiStatus } : null}
          showSubmitButton
          onSubmit={handleSubmit}
          onCreatingChange={setCreating}
          onAfterDeleteRequest={() => setOpen(false)}
        />
      </Paper>
    </Modal>
  );
};

export default TemplateFormModal;
